import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { BsHouseDoor, BsCurrencyDollar, BsArrowUpCircle, BsEye, BsX } from 'react-icons/bs';

interface QuickPreferenceSurveyProps {
  onComplete: (preferences: UserPreferences) => void;
  onSkip: () => void;
}

interface UserPreferences {
  purpose: string;
  priceRange?: string;
  timeline?: string;
  location?: string;
}

const QuickPreferenceSurvey: React.FC<QuickPreferenceSurveyProps> = ({ onComplete, onSkip }) => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [preferences, setPreferences] = useState<UserPreferences>({
    purpose: ''
  });

  useEffect(() => {
    // Check if user has already completed survey
    const hasCompletedSurvey = localStorage.getItem('chatRealEstateSurveyCompleted');
    if (!hasCompletedSurvey) {
      // Show survey after a short delay for better UX
      setTimeout(() => setShow(true), 1500);
    }
  }, []);

  const purposes = [
    {
      id: 'first-time',
      icon: <BsHouseDoor size={40} />,
      title: 'First-Time Buyer',
      description: 'Looking for my first home',
      color: '#4CAF50'
    },
    {
      id: 'investment',
      icon: <BsCurrencyDollar size={40} />,
      title: 'Investment Property',
      description: 'Building my portfolio',
      color: '#2196F3'
    },
    {
      id: 'upgrade',
      icon: <BsArrowUpCircle size={40} />,
      title: 'Upgrading Home',
      description: 'Ready for something bigger',
      color: '#9C27B0'
    },
    {
      id: 'browsing',
      icon: <BsEye size={40} />,
      title: 'Just Browsing',
      description: 'Exploring the market',
      color: '#FF9800'
    }
  ];

  const priceRanges = [
    { id: 'under-300k', label: 'Under $300k', filter: { maxPrice: 300000 } },
    { id: '300-500k', label: '$300k - $500k', filter: { minPrice: 300000, maxPrice: 500000 } },
    { id: '500-700k', label: '$500k - $700k', filter: { minPrice: 500000, maxPrice: 700000 } },
    { id: '700k-plus', label: '$700k+', filter: { minPrice: 700000 } }
  ];

  const timelines = [
    { id: 'asap', label: 'ASAP', description: 'Ready to move now' },
    { id: '3-months', label: 'Next 3 months', description: 'Actively searching' },
    { id: '6-months', label: 'Next 6 months', description: 'Planning ahead' },
    { id: 'just-looking', label: 'Just researching', description: 'No rush' }
  ];

  const handlePurposeSelect = (purpose: string) => {
    setPreferences({ ...preferences, purpose });
    
    // Skip directly to completion for "just browsing"
    if (purpose === 'browsing') {
      handleComplete({ ...preferences, purpose });
    } else {
      setStep(2);
    }
  };

  const handlePriceSelect = (priceRange: string) => {
    setPreferences({ ...preferences, priceRange });
    setStep(3);
  };

  const handleTimelineSelect = (timeline: string) => {
    const finalPreferences = { ...preferences, timeline };
    setPreferences(finalPreferences);
    handleComplete(finalPreferences);
  };

  const handleComplete = (prefs: UserPreferences) => {
    localStorage.setItem('chatRealEstateSurveyCompleted', 'true');
    localStorage.setItem('chatRealEstatePreferences', JSON.stringify(prefs));
    setShow(false);
    onComplete(prefs);
  };

  const handleSkip = () => {
    localStorage.setItem('chatRealEstateSurveyCompleted', 'skipped');
    setShow(false);
    onSkip();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleSkip}
      centered
      size="lg"
      className="preference-survey-modal"
    >
      <Modal.Header className="border-0 pb-0">
        <div className="w-100">
          <Button
            variant="link"
            className="float-end text-muted p-0"
            onClick={handleSkip}
          >
            <BsX size={24} />
          </Button>
          <Modal.Title className="text-center w-100">
            {step === 1 && (
              <>
                <h3 className="mb-2">Welcome! What brings you here today? 👋</h3>
                <p className="text-muted fs-6 fw-normal">Help us show you the most relevant properties</p>
              </>
            )}
            {step === 2 && (
              <>
                <h3 className="mb-2">What's your budget range? 💰</h3>
                <p className="text-muted fs-6 fw-normal">We'll focus on properties in your price range</p>
              </>
            )}
            {step === 3 && (
              <>
                <h3 className="mb-2">When are you looking to move? 📅</h3>
                <p className="text-muted fs-6 fw-normal">This helps us prioritize your search</p>
              </>
            )}
          </Modal.Title>
        </div>
      </Modal.Header>
      
      <Modal.Body className="pt-4">
        {step === 1 && (
          <Row className="g-3">
            {purposes.map((purpose) => (
              <Col key={purpose.id} xs={6} md={3}>
                <Button
                  variant="outline-primary"
                  className="purpose-card w-100 h-100 p-3"
                  onClick={() => handlePurposeSelect(purpose.id)}
                  style={{ borderColor: purpose.color }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = purpose.color;
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = purpose.color;
                  }}
                >
                  <div className="text-center">
                    <div style={{ color: 'inherit' }}>{purpose.icon}</div>
                    <h6 className="mt-2 mb-1">{purpose.title}</h6>
                    <small className="text-muted">{purpose.description}</small>
                  </div>
                </Button>
              </Col>
            ))}
          </Row>
        )}

        {step === 2 && (
          <Row className="g-3">
            {priceRanges.map((range) => (
              <Col key={range.id} xs={6} md={3}>
                <Button
                  variant="outline-primary"
                  className="price-range-card w-100 p-3"
                  onClick={() => handlePriceSelect(range.id)}
                >
                  <h5 className="mb-0">{range.label}</h5>
                </Button>
              </Col>
            ))}
          </Row>
        )}

        {step === 3 && (
          <Row className="g-3">
            {timelines.map((timeline) => (
              <Col key={timeline.id} xs={6} md={3}>
                <Button
                  variant="outline-primary"
                  className="timeline-card w-100 p-3"
                  onClick={() => handleTimelineSelect(timeline.id)}
                >
                  <h6 className="mb-1">{timeline.label}</h6>
                  <small className="text-muted">{timeline.description}</small>
                </Button>
              </Col>
            ))}
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <div className="w-100 text-center">
          {step > 1 && (
            <Button 
              variant="link" 
              onClick={() => setStep(step - 1)}
              className="me-3"
            >
              ← Back
            </Button>
          )}
          <Button 
            variant="link" 
            onClick={handleSkip}
            className="text-muted"
          >
            Skip for now
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QuickPreferenceSurvey;